// generateFlowTestPDF.js
// Import this and call generateFlowTestPDF(flowTest)

import { LOGO_B64, COMPANY, C_RED, C_DARK, C_WHITE, C_GREY_BG, C_GREY_TEXT } from "./generateInspectionPDF";

const C_GREEN = [39, 174, 96];
const C_CHART_BLUE = [41, 98, 185];
const C_CHART_GRID = [198, 219, 245];

export async function generateFlowTestPDF(flowTest, opts = {}) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const pageW = 210;
  const margin = 10;
  const contentW = pageW - margin * 2;
  let y = margin;

  const dateStr = flowTest.tested_at
    ? new Date(flowTest.tested_at).toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" });

  const drawFooter = () => {
    doc.setFontSize(8);
    doc.setTextColor(...C_GREY_TEXT);
    doc.text("Page " + doc.internal.getNumberOfPages(), pageW - margin, 289, { align: "right" });
    doc.setDrawColor(...C_GREY_TEXT);
    doc.setLineWidth(0.3);
    doc.line(margin, 285, pageW - margin, 285);
  };

  // ── HEADER ──────────────────────────────────────────────────────────────────
  try {
    doc.addImage("data:image/jpeg;base64," + LOGO_B64, "JPEG", margin, y, 72, 32);
  } catch (e) {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_RED);
    doc.text("STANDARD FIRE PROTECTION", margin, y + 10);
  }
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_DARK);
  const cx = pageW - margin;
  doc.text(COMPANY.accreditation, cx, y + 4, { align: "right" });
  doc.text(COMPANY.address, cx, y + 9, { align: "right" });
  doc.text(COMPANY.phone, cx, y + 14, { align: "right" });
  doc.text(COMPANY.email, cx, y + 19, { align: "right" });
  y += 35;

  doc.setDrawColor(...C_RED);
  doc.setLineWidth(0.8);
  doc.line(margin, y, pageW - margin, y);
  y += 9;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text("Fire Hose Reel Flow Test Report", pageW / 2, y, { align: "center" });
  y += 7;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text((flowTest.site_address || flowTest.site_name) + " — " + dateStr, pageW / 2, y, { align: "center" });
  y += 10;

  // ── SITE DETAILS BOX ────────────────────────────────────────────────────────
  doc.setFillColor(...C_GREY_BG);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentW, 28, 2, 2, "FD");
  const c1 = margin + 5;
  const c2 = margin + contentW / 2 + 5;
  let dy = y + 7;

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("SITE", c1, dy);
  doc.text("STANDARDS", c2, dy);
  dy += 5;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_DARK);
  doc.setFontSize(9);
  doc.text(flowTest.site_name, c1, dy);
  doc.text("AS 1851 / AS 2441", c2, dy);
  dy += 5;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("ADDRESS", c1, dy);
  doc.text("TECHNICIAN", c2, dy);
  dy += 5;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_DARK);
  doc.setFontSize(9);
  doc.text(flowTest.site_address || "—", c1, dy);
  doc.text(flowTest.assigned_to_name || "—", c2, dy);
  y += 34;

  // ── OVERALL RESULT BANNER ───────────────────────────────────────────────────
  const passed = flowTest.status === "passed";
  doc.setFillColor(...(passed ? C_GREEN : C_RED));
  doc.roundedRect(margin, y, contentW, 9, 2, 2, "F");
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_WHITE);
  const banner = passed
    ? "FLOW TEST PASSED - Meets AS 1851 / AS 2441 requirements"
    : "FLOW TEST FAILED - Does not meet AS 1851 / AS 2441 requirements";
  doc.text(banner, pageW / 2, y + 6, { align: "center" });
  y += 18;

  // ── COMPLIANCE GRAPHS ───────────────────────────────────────────────────────
  // Two separate charts, one per measurement (flow, pressure), each value
  // plotted against attempt number with its reading labeled directly above
  // the point and a single dashed minimum-threshold line - matches the
  // user's own sketch of the expected layout exactly.
  const readings = (flowTest.readings || [])
    .slice()
    .sort((a, b) => a.seconds_elapsed - b.seconds_elapsed);

  const minP = Number(flowTest.min_pressure_kpa ?? 198);
  const minFlowThreshold = Number(flowTest.min_flow_ls ?? 0.33);

  const drawDashedLine = (x0, y0, x1, y1, dash = 2, gap = 1.5) => {
    const dx = x1 - x0, dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    const steps = Math.max(1, Math.floor(len / (dash + gap)));
    const ux = dx / len, uy = dy / len;
    for (let i = 0; i < steps; i++) {
      const sx = x0 + ux * i * (dash + gap);
      const sy = y0 + uy * i * (dash + gap);
      const ex = sx + ux * dash;
      const ey = sy + uy * dash;
      doc.line(sx, sy, ex, ey);
    }
  };

  // Picks a "nice" grid step (1/2/5 x a power of 10) for a given axis
  // range, matching how graph-paper style charts space their gridlines
  // regardless of the actual data's magnitude.
  const niceStep = (range, targetTicks = 5) => {
    const rough = range / targetTicks;
    const mag = Math.pow(10, Math.floor(Math.log10(rough)));
    const norm = rough / mag;
    const step = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
    return step * mag;
  };

  const drawAttemptChart = ({ title, unit, values, threshold, decimals }) => {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_DARK);
    doc.text(title, margin, y);
    y += 7;

    const dataMin = Math.min(...values, threshold);
    const dataMax = Math.max(...values, threshold);
    const pad = (dataMax - dataMin || dataMax * 0.1) * 0.5;
    const rawMin = Math.max(0, dataMin - pad);
    const rawMax = dataMax + pad;
    const step = niceStep(rawMax - rawMin, 8);
    const axisMin = Math.floor(rawMin / step) * step;
    const axisMax = Math.ceil(rawMax / step) * step;

    const chartX = margin + 16;
    const chartW = contentW - 20;
    const chartH = 42;
    const chartYTop = y;
    const chartYBottom = chartYTop + chartH;

    const scaleX = (i) => chartX + ((i) / (values.length - 1 || 1)) * chartW;
    const scaleY = (v) => chartYBottom - ((v - axisMin) / (axisMax - axisMin)) * chartH;

    // White chart background
    doc.setFillColor(...C_WHITE);
    doc.rect(chartX, chartYTop, chartW, chartH, "F");

    // Blue grid-paper gridlines
    doc.setDrawColor(...C_CHART_GRID);
    doc.setLineWidth(0.2);
    for (let v = axisMin; v <= axisMax + 1e-9; v += step) {
      const gy = scaleY(v);
      doc.line(chartX, gy, chartX + chartW, gy);
    }
    values.forEach((_, i) => {
      const gx = scaleX(i);
      doc.line(gx, chartYTop, gx, chartYBottom);
    });

    // Crisp blue chart border
    doc.setDrawColor(...C_CHART_BLUE);
    doc.setLineWidth(0.5);
    doc.rect(chartX, chartYTop, chartW, chartH);

    // Dashed minimum-threshold reference line
    doc.setDrawColor(...C_DARK);
    doc.setLineWidth(0.4);
    drawDashedLine(chartX, scaleY(threshold), chartX + chartW, scaleY(threshold));

    // Y-axis labels
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_GREY_TEXT);
    for (let v = axisMin; v <= axisMax + 1e-9; v += step) {
      doc.text(v.toFixed(decimals), chartX - 2, scaleY(v) + 1, { align: "right" });
    }

    // X-axis: attempt number
    values.forEach((_, i) => {
      doc.text(`Attempt ${i + 1}`, scaleX(i), chartYBottom + 5, { align: "center" });
    });

    // Connecting line + markers, in the reference-style blue
    doc.setDrawColor(...C_CHART_BLUE);
    doc.setLineWidth(0.8);
    values.forEach((v, i) => {
      if (i === 0) return;
      doc.line(scaleX(i - 1), scaleY(values[i - 1]), scaleX(i), scaleY(v));
    });
    doc.setFillColor(...C_CHART_BLUE);
    values.forEach((v, i) => {
      doc.circle(scaleX(i), scaleY(v), 1.4, "F");
    });

    // Value labels directly above each point
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_CHART_BLUE);
    values.forEach((v, i) => {
      doc.text(v.toFixed(decimals), scaleX(i), scaleY(v) - 3, { align: "center" });
    });

    y = chartYBottom + 20;
  };

  if (readings.length > 0) {
    const flows = readings.map((r) => Number(r.flow_rate_ls));
    const pressures = readings.map((r) => Number(r.pressure_kpa));

    drawAttemptChart({
      title: "Flow Rate (L/s) - AS 1851 minimum 0.33 L/s",
      unit: "L/s",
      values: flows,
      threshold: minFlowThreshold,
      decimals: 2,
    });
    drawAttemptChart({
      title: "Pressure (kPa) - AS 2441 minimum 198 kPa",
      unit: "kPa",
      values: pressures,
      threshold: minP,
      decimals: 0,
    });
  } else {
    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text("No readings recorded for this test.", margin, y);
    y += 10;
  }

  // ── NOTES ───────────────────────────────────────────────────────────────────
  if (flowTest.notes && flowTest.notes.trim()) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bolditalic");
    doc.setTextColor(...C_DARK);
    doc.text("Notes:", margin, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    const noteLines = doc.splitTextToSize(flowTest.notes, contentW);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 4.5 + 6;
  }

  // ── SIGNATURE ───────────────────────────────────────────────────────────────
  y = Math.max(y + 10, 245);
  const sigW = (contentW - 20) / 2;
  const sx0 = margin;
  const sx1 = margin + sigW + 20;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(sx0, y, sx0 + sigW, y);
  doc.line(sx1, y, sx1 + sigW, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...C_DARK);
  doc.text(flowTest.assigned_to_name || "", sx0, y - 4);
  doc.text(dateStr, sx1, y - 4);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("TECHNICIAN NAME", sx0, y + 4);
  doc.text("DATE TESTED", sx1, y + 4);

  drawFooter();

  if (opts.noDownload) {
    return doc.output("blob");
  }
  doc.save(`FHR_Flow_Test_${(flowTest.site_name || "site").replace(/[^a-z0-9]/gi, "_")}.pdf`);
  return doc.output("blob");
}
